import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Plus, Trash2, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';

const InvoiceGenerator = () => {
  const invoiceRef = useRef(null);
  const [invoice, setInvoice] = useState({
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    from: {
      name: '',
      address: '',
      phone: '',
      email: ''
    },
    to: {
      name: '',
      address: '',
      phone: '',
      email: ''
    },
    items: [{ description: '', quantity: 1, rate: 0 }],
    notes: '',
    taxRate: 0
  });

  const addItem = () => {
    setInvoice({
      ...invoice,
      items: [...invoice.items, { description: '', quantity: 1, rate: 0 }]
    });
  };

  const removeItem = (index) => {
    if (invoice.items.length > 1) {
      setInvoice({
        ...invoice,
        items: invoice.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index][field] = value;
    setInvoice({ ...invoice, items: newItems });
  };

  const subtotal = invoice.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  const tax = (subtotal * invoice.taxRate) / 100;
  const total = subtotal + tax;

  const printInvoice = () => {
    const printContent = invoiceRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoice.invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            .invoice-header { display: flex; justify-content: space-between; margin-bottom: 30px; }
            .invoice-title { font-size: 28px; font-weight: bold; color: #2563eb; }
            .invoice-meta { text-align: right; }
            .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
            .party { width: 45%; }
            .party-title { font-weight: bold; margin-bottom: 10px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background: #f5f5f5; font-weight: 600; }
            .totals { text-align: right; }
            .totals div { margin: 8px 0; }
            .total-row { font-size: 18px; font-weight: bold; color: #2563eb; }
            .notes { margin-top: 30px; padding: 15px; background: #f9f9f9; border-radius: 8px; }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <div class="invoice-title">INVOICE</div>
            <div class="invoice-meta">
              <div><strong>Invoice #:</strong> ${invoice.invoiceNumber}</div>
              <div><strong>Date:</strong> ${invoice.date}</div>
              ${invoice.dueDate ? `<div><strong>Due Date:</strong> ${invoice.dueDate}</div>` : ''}
            </div>
          </div>
          <div class="parties">
            <div class="party">
              <div class="party-title">From:</div>
              <div><strong>${invoice.from.name || '-'}</strong></div>
              <div>${invoice.from.address || ''}</div>
              <div>${invoice.from.phone || ''}</div>
              <div>${invoice.from.email || ''}</div>
            </div>
            <div class="party">
              <div class="party-title">Bill To:</div>
              <div><strong>${invoice.to.name || '-'}</strong></div>
              <div>${invoice.to.address || ''}</div>
              <div>${invoice.to.phone || ''}</div>
              <div>${invoice.to.email || ''}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items.map(item => `
                <tr>
                  <td>${item.description || '-'}</td>
                  <td>${item.quantity}</td>
                  <td>₹${item.rate.toLocaleString()}</td>
                  <td>₹${(item.quantity * item.rate).toLocaleString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="totals">
            <div><strong>Subtotal:</strong> ₹${subtotal.toLocaleString()}</div>
            ${invoice.taxRate > 0 ? `<div><strong>Tax (${invoice.taxRate}%):</strong> ₹${tax.toLocaleString()}</div>` : ''}
            <div class="total-row"><strong>Total:</strong> ₹${total.toLocaleString()}</div>
          </div>
          ${invoice.notes ? `<div class="notes"><strong>Notes:</strong><br>${invoice.notes}</div>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="invoice-generator-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 via-background to-blue-500/10 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/tools" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Invoice Generator</h1>
              <p className="text-muted-foreground">Create professional invoices instantly</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div ref={invoiceRef} className="glass-card p-6 space-y-6">
          {/* Invoice Details */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Invoice Number</label>
              <input
                type="text"
                value={invoice.invoiceNumber}
                onChange={(e) => setInvoice({ ...invoice, invoiceNumber: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                value={invoice.date}
                onChange={(e) => setInvoice({ ...invoice, date: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Due Date</label>
              <input
                type="date"
                value={invoice.dueDate}
                onChange={(e) => setInvoice({ ...invoice, dueDate: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
          </div>

          {/* From / To */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-blue-500">From (Your Details)</h3>
              <input
                type="text"
                placeholder="Business Name"
                value={invoice.from.name}
                onChange={(e) => setInvoice({ ...invoice, from: { ...invoice.from, name: e.target.value } })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
              <input
                type="text"
                placeholder="Address"
                value={invoice.from.address}
                onChange={(e) => setInvoice({ ...invoice, from: { ...invoice.from, address: e.target.value } })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="tel"
                  placeholder="Phone"
                  value={invoice.from.phone}
                  onChange={(e) => setInvoice({ ...invoice, from: { ...invoice.from, phone: e.target.value } })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={invoice.from.email}
                  onChange={(e) => setInvoice({ ...invoice, from: { ...invoice.from, email: e.target.value } })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-green-500">Bill To (Client)</h3>
              <input
                type="text"
                placeholder="Client Name"
                value={invoice.to.name}
                onChange={(e) => setInvoice({ ...invoice, to: { ...invoice.to, name: e.target.value } })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
              <input
                type="text"
                placeholder="Address"
                value={invoice.to.address}
                onChange={(e) => setInvoice({ ...invoice, to: { ...invoice.to, address: e.target.value } })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="tel"
                  placeholder="Phone"
                  value={invoice.to.phone}
                  onChange={(e) => setInvoice({ ...invoice, to: { ...invoice.to, phone: e.target.value } })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={invoice.to.email}
                  onChange={(e) => setInvoice({ ...invoice, to: { ...invoice.to, email: e.target.value } })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div>
            <h3 className="font-semibold mb-3">Items</h3>
            <div className="space-y-3">
              {invoice.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    className="col-span-5 px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 0)}
                    className="col-span-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                  <input
                    type="number"
                    placeholder="Rate"
                    value={item.rate}
                    onChange={(e) => updateItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                    className="col-span-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                  <div className="col-span-2 text-right font-semibold">
                    ₹{(item.quantity * item.rate).toLocaleString()}
                  </div>
                  <button
                    onClick={() => removeItem(idx)}
                    className="col-span-1 p-3 text-red-500 hover:bg-red-500/20 rounded-xl transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addItem}
              className="mt-3 px-4 py-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>

          {/* Tax & Total */}
          <div className="flex justify-end">
            <div className="w-64 space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-semibold">₹{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-muted-foreground">Tax %:</span>
                <input
                  type="number"
                  value={invoice.taxRate}
                  onChange={(e) => setInvoice({ ...invoice, taxRate: parseFloat(e.target.value) || 0 })}
                  className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-center"
                />
                <span className="font-semibold">₹{tax.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-lg pt-3 border-t border-white/10">
                <span className="font-bold">Total:</span>
                <span className="font-bold text-primary">₹{total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              value={invoice.notes}
              onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
              placeholder="Payment terms, bank details, or any additional notes..."
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={printInvoice}
              className="flex-1 py-4 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
              data-testid="print-invoice-btn"
            >
              <Printer className="w-5 h-5" />
              Print / Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceGenerator;
